import React, { useState, useMemo, useCallback } from 'react';
import './ResumeForm.css';
import ThemeCustomizer from './ThemeCustomizer';

const SectionIcon = {
  personalInfo: '👤',
  summary: '📝',
  education: '🎓',
  skills: '⚡',
  professionalExperience: '💼',
  projects: '🔧',
  certifications: '🏅',
  awards: '🏆'
};

const getSectionCompletion = (data, section) => {
  if (section === 'personalInfo') {
    const p = data.personalInfo;
    return [p.fullName, p.email, p.phone].filter(Boolean).length;
  }
  if (section === 'summary') return data.summary ? 1 : 0;
  if (section === 'skills') return data.skills.length;
  if (section === 'education') return data.education.filter(e => e.school).length;
  if (section === 'professionalExperience') return data.professionalExperience.filter(e => e.position).length;
  if (section === 'projects') return data.projects.filter(p => p.title).length;
  if (section === 'certifications') return data.certifications.filter(c => c.name).length;
  if (section === 'awards') return data.awards.filter(a => a.title).length;
  return 0;
};

const getSectionTotal = (data, section) => {
  if (section === 'personalInfo') return 3;
  if (section === 'summary') return 1;
  if (section === 'skills') return Math.max(data.skills.length, 1);
  if (section === 'education') return Math.max(data.education.length, 1);
  if (section === 'professionalExperience') return Math.max(data.professionalExperience.length, 1);
  if (section === 'projects') return Math.max(data.projects.length, 1);
  if (section === 'certifications') return Math.max(data.certifications.length, 1);
  if (section === 'awards') return Math.max(data.awards.length, 1);
  return 1;
};

const AccordionSection = ({ title, section, icon, children, isOpen, onToggle, completion, total, visible, onToggleVisibility }) => {
  const pct = total > 0 ? Math.min(completion / total, 1) : 0;
  const isComplete = completion > 0;

  return (
    <div className={`accordion-section ${isOpen ? 'open' : ''}`}>
      <div className="accordion-header" onClick={onToggle}>
        <div className="accordion-header-left">
          <span className="accordion-icon">{icon}</span>
          <span className="accordion-title">{title}</span>
          {isComplete && <span className="accordion-check">✓</span>}
        </div>
        <div className="accordion-header-right">
          <div className="section-progress" title={`${completion}/${total} fields filled`}>
            <div className="progress-bar" style={{ width: `${pct * 100}%` }} />
          </div>
          <button className={`visibility-toggle ${visible ? 'visible' : 'hidden'}`}
            onClick={(e) => { e.stopPropagation(); onToggleVisibility(section); }}
            title={visible ? 'Hide section from resume' : 'Show section in resume'}>
            {visible ? '👁' : '👁‍🗨'}
          </button>
          <span className={`accordion-arrow ${isOpen ? 'open' : ''}`}>▾</span>
        </div>
      </div>
      {isOpen && <div className="accordion-body">{children}</div>}
    </div>
  );
};

const defaultSectionOrder = ['personalInfo', 'summary', 'education', 'skills', 'professionalExperience', 'projects', 'certifications', 'awards'];

const ResumeForm = ({ data, onUpdate, onClear, sectionVisibility, onToggleSection, sectionOrder, onReorderSection, onResetOrder }) => {
  const [expanded, setExpanded] = useState({ personalInfo: true });
  const [skillInput, setSkillInput] = useState(data.skills.join(', '));
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const toggleSection = (section) => {
    setExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleChange = (section, value) => {
    onUpdate({ ...data, [section]: value });
  };

  const handleDragStart = useCallback((index) => {
    setDragIndex(index);
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((index) => {
    if (dragIndex !== null && dragIndex !== index) {
      onReorderSection(dragIndex, index);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  }, [dragIndex, onReorderSection]);

  const handlePersonalChange = (field, value) => {
    onUpdate({ ...data, personalInfo: { ...data.personalInfo, [field]: value } });
  };

  const handleThemeChange = (newTheme) => {
    onUpdate({ ...data, theme: newTheme });
  };

  const handleSkillsChange = (e) => {
    setSkillInput(e.target.value);
    handleChange('skills', e.target.value.split(',').map(s => s.trim()).filter(Boolean));
  };

  const handleArrayAdd = (section, emptyItem) => {
    handleChange(section, [...(data[section] || []), emptyItem]);
  };

  const handleArrayRemove = (section, index) => {
    handleChange(section, data[section].filter((_, i) => i !== index));
  };

  const handleArrayFieldChange = (section, index, field, value) => {
    const arr = [...data[section]];
    arr[index] = { ...arr[index], [field]: value };
    handleChange(section, arr);
  };

  const commonInput = {
    spellCheck: "true",
    lang: "en-US",
    autoCapitalize: "words"
  };

  const sectionMetaMap = {
    personalInfo: { title: 'Personal Information', icon: SectionIcon.personalInfo },
    summary: { title: 'Professional Summary', icon: SectionIcon.summary },
    education: { title: 'Education', icon: SectionIcon.education },
    skills: { title: 'Skills', icon: SectionIcon.skills },
    professionalExperience: { title: 'Experience', icon: SectionIcon.professionalExperience },
    projects: { title: 'Projects', icon: SectionIcon.projects },
    certifications: { title: 'Certifications', icon: SectionIcon.certifications },
    awards: { title: 'Awards', icon: SectionIcon.awards }
  };

  const sectionKeys = Object.keys(sectionMetaMap);
  const totalCompletion = useMemo(() => {
    let filled = 0, total = 0;
    sectionKeys.forEach(key => {
      filled += getSectionCompletion(data, key);
      total += getSectionTotal(data, key);
    });
    return total > 0 ? Math.round((filled / total) * 100) : 0;
  }, [data, sectionKeys]);

  return (
    <div className="resume-form">
      <div className="form-global-progress">
        <div className="global-progress-bar">
          <div className="global-progress-fill" style={{ width: `${totalCompletion}%` }} />
        </div>
        <span className="global-progress-text">{totalCompletion}% complete</span>
      </div>

      <ThemeCustomizer theme={data.theme} onUpdate={handleThemeChange} onClear={onClear} />

      <div className="section-order-header">
        <span className="section-order-label">Sections <span className="drag-hint">(drag to reorder)</span></span>
        <button className="reset-order-btn" onClick={onResetOrder}>Reset Order</button>
      </div>

      {sectionOrder.map((key, index) => {
        const meta = sectionMetaMap[key];
        if (!meta) return null;
        const isDragOver = dragOverIndex === index && dragIndex !== index;

        return (
          <div
            key={key}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={() => handleDrop(index)}
            className={`draggable-section ${isDragOver ? 'drag-over' : ''} ${dragIndex === index ? 'dragging' : ''}`}
          >
        <AccordionSection
          title={meta.title}
          section={key}
          icon={meta.icon}
          isOpen={expanded[key]}
          onToggle={() => toggleSection(key)}
          completion={getSectionCompletion(data, key)}
          total={getSectionTotal(data, key)}
          visible={sectionVisibility[key]}
          onToggleVisibility={onToggleSection}
        >
          {key === 'personalInfo' && (
            <div className="form-content">
              <input {...commonInput} type="text" placeholder="Full Name *"
                value={data.personalInfo.fullName}
                onChange={(e) => handlePersonalChange('fullName', e.target.value)} />
              <input {...commonInput} type="text" placeholder="Professional Title (e.g. Software Engineer)"
                value={data.personalInfo.title || ''}
                onChange={(e) => handlePersonalChange('title', e.target.value)} />
              <input {...commonInput} type="email" placeholder="Email *"
                value={data.personalInfo.email}
                onChange={(e) => handlePersonalChange('email', e.target.value)} />
              <input {...commonInput} type="tel" placeholder="Phone *"
                value={data.personalInfo.phone}
                onChange={(e) => handlePersonalChange('phone', e.target.value)} />
              <textarea {...commonInput} placeholder="Address"
                value={data.personalInfo.address}
                onChange={(e) => handlePersonalChange('address', e.target.value)} />
              <input {...commonInput} type="url" placeholder="LinkedIn URL"
                value={data.personalInfo.linkedin}
                onChange={(e) => handlePersonalChange('linkedin', e.target.value)} />
              <input {...commonInput} type="url" placeholder="GitHub URL"
                value={data.personalInfo.github || ''}
                onChange={(e) => handlePersonalChange('github', e.target.value)} />
            </div>
          )}

          {key === 'summary' && (
            <div className="form-content">
              <textarea {...commonInput} placeholder="Write a brief professional summary (2-4 sentences highlighting your key strengths and career objectives)"
                value={data.summary || ''} rows={4}
                onChange={(e) => handleChange('summary', e.target.value)} />
            </div>
          )}

          {key === 'education' && (
            <div className="form-content">
              <button className="add-item-btn" onClick={() => handleArrayAdd('education', { school: '', degree: '', startDate: '', endDate: '', description: '' })}>
                + Add Education
              </button>
              {data.education.map((edu, i) => (
                <div key={i} className="form-group-item">
                  <div className="form-group-header">
                    <span className="group-number">{i + 1}</span>
                    <button className="remove-item-btn" onClick={() => handleArrayRemove('education', i)}>×</button>
                  </div>
                  <input {...commonInput} type="text" placeholder="School / University"
                    value={edu.school}
                    onChange={(e) => handleArrayFieldChange('education', i, 'school', e.target.value)} />
                  <input {...commonInput} type="text" placeholder="Degree"
                    value={edu.degree}
                    onChange={(e) => handleArrayFieldChange('education', i, 'degree', e.target.value)} />
                  <div className="date-row">
                    <input {...commonInput} type="text" placeholder="Start (e.g. 2022)"
                      value={edu.startDate}
                      onChange={(e) => handleArrayFieldChange('education', i, 'startDate', e.target.value)} />
                    <input {...commonInput} type="text" placeholder="End (e.g. 2026)"
                      value={edu.endDate}
                      onChange={(e) => handleArrayFieldChange('education', i, 'endDate', e.target.value)} />
                  </div>
                  <textarea {...commonInput} placeholder="Description (optional)"
                    value={edu.description}
                    onChange={(e) => handleArrayFieldChange('education', i, 'description', e.target.value)} />
                </div>
              ))}
            </div>
          )}

          {key === 'skills' && (
            <div className="form-content">
              <div className="skills-input-wrapper">
                <input {...commonInput} type="text" placeholder="Enter skills separated by commas"
                  value={skillInput} onChange={handleSkillsChange} />
                <div className="skills-preview">
                  {data.skills.map((s, i) => (
                    <span key={i} className="skill-chip">
                      {s}
                      <button className="skill-chip-remove" onClick={() => {
                        const newSkills = data.skills.filter((_, idx) => idx !== i);
                        setSkillInput(newSkills.join(', '));
                        handleChange('skills', newSkills);
                      }}>×</button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {key === 'professionalExperience' && (
            <div className="form-content">
              <button className="add-item-btn" onClick={() => handleArrayAdd('professionalExperience', { company: '', position: '', startDate: '', endDate: '', description: '', location: '' })}>
                + Add Experience
              </button>
              {data.professionalExperience.map((exp, i) => (
                <div key={i} className="form-group-item">
                  <div className="form-group-header">
                    <span className="group-number">{i + 1}</span>
                    <button className="remove-item-btn" onClick={() => handleArrayRemove('professionalExperience', i)}>×</button>
                  </div>
                  <input {...commonInput} type="text" placeholder="Company Name"
                    value={exp.company}
                    onChange={(e) => handleArrayFieldChange('professionalExperience', i, 'company', e.target.value)} />
                  <input {...commonInput} type="text" placeholder="Position"
                    value={exp.position}
                    onChange={(e) => handleArrayFieldChange('professionalExperience', i, 'position', e.target.value)} />
                  <div className="date-row">
                    <input {...commonInput} type="text" placeholder="Start Date"
                      value={exp.startDate}
                      onChange={(e) => handleArrayFieldChange('professionalExperience', i, 'startDate', e.target.value)} />
                    <input {...commonInput} type="text" placeholder="End Date"
                      value={exp.endDate}
                      onChange={(e) => handleArrayFieldChange('professionalExperience', i, 'endDate', e.target.value)} />
                  </div>
                  <input {...commonInput} type="text" placeholder="Location"
                    value={exp.location}
                    onChange={(e) => handleArrayFieldChange('professionalExperience', i, 'location', e.target.value)} />
                  <textarea {...commonInput} placeholder="Describe your responsibilities and achievements"
                    value={exp.description}
                    onChange={(e) => handleArrayFieldChange('professionalExperience', i, 'description', e.target.value)} />
                </div>
              ))}
            </div>
          )}

          {key === 'projects' && (
            <div className="form-content">
              <button className="add-item-btn" onClick={() => handleArrayAdd('projects', { title: '', description: '', technologies: '', link: '', startDate: '', endDate: '' })}>
                + Add Project
              </button>
              {data.projects.map((project, i) => (
                <div key={i} className="form-group-item">
                  <div className="form-group-header">
                    <span className="group-number">{i + 1}</span>
                    <button className="remove-item-btn" onClick={() => handleArrayRemove('projects', i)}>×</button>
                  </div>
                  <input {...commonInput} type="text" placeholder="Project Title"
                    value={project.title}
                    onChange={(e) => handleArrayFieldChange('projects', i, 'title', e.target.value)} />
                  <textarea {...commonInput} placeholder="Project Description"
                    value={project.description}
                    onChange={(e) => handleArrayFieldChange('projects', i, 'description', e.target.value)} />
                  <input {...commonInput} type="text" placeholder="Technologies Used (comma separated)"
                    value={project.technologies}
                    onChange={(e) => handleArrayFieldChange('projects', i, 'technologies', e.target.value)} />
                  <div className="date-row">
                    <input {...commonInput} type="text" placeholder="Start Date"
                      value={project.startDate}
                      onChange={(e) => handleArrayFieldChange('projects', i, 'startDate', e.target.value)} />
                    <input {...commonInput} type="text" placeholder="End Date"
                      value={project.endDate}
                      onChange={(e) => handleArrayFieldChange('projects', i, 'endDate', e.target.value)} />
                  </div>
                  <input {...commonInput} type="url" placeholder="Project Link (optional)"
                    value={project.link}
                    onChange={(e) => handleArrayFieldChange('projects', i, 'link', e.target.value)} />
                </div>
              ))}
            </div>
          )}

          {key === 'certifications' && (
            <div className="form-content">
              <button className="add-item-btn" onClick={() => handleArrayAdd('certifications', { name: '', issuer: '', date: '', expiryDate: '', credentialId: '', credentialURL: '' })}>
                + Add Certification
              </button>
              {data.certifications.map((cert, i) => (
                <div key={i} className="form-group-item">
                  <div className="form-group-header">
                    <span className="group-number">{i + 1}</span>
                    <button className="remove-item-btn" onClick={() => handleArrayRemove('certifications', i)}>×</button>
                  </div>
                  <input {...commonInput} type="text" placeholder="Certification Name"
                    value={cert.name}
                    onChange={(e) => handleArrayFieldChange('certifications', i, 'name', e.target.value)} />
                  <input {...commonInput} type="text" placeholder="Issuing Organization"
                    value={cert.issuer}
                    onChange={(e) => handleArrayFieldChange('certifications', i, 'issuer', e.target.value)} />
                  <div className="date-row">
                    <input {...commonInput} type="text" placeholder="Issue Date"
                      value={cert.date}
                      onChange={(e) => handleArrayFieldChange('certifications', i, 'date', e.target.value)} />
                    <input {...commonInput} type="text" placeholder="Expiry Date (optional)"
                      value={cert.expiryDate}
                      onChange={(e) => handleArrayFieldChange('certifications', i, 'expiryDate', e.target.value)} />
                  </div>
                  <input {...commonInput} type="text" placeholder="Credential ID (optional)"
                    value={cert.credentialId}
                    onChange={(e) => handleArrayFieldChange('certifications', i, 'credentialId', e.target.value)} />
                  <input {...commonInput} type="url" placeholder="Credential URL (optional)"
                    value={cert.credentialURL}
                    onChange={(e) => handleArrayFieldChange('certifications', i, 'credentialURL', e.target.value)} />
                </div>
              ))}
            </div>
          )}

          {key === 'awards' && (
            <div className="form-content">
              <button className="add-item-btn" onClick={() => handleArrayAdd('awards', { title: '', issuer: '', date: '', description: '' })}>
                + Add Award
              </button>
              {data.awards.map((award, i) => (
                <div key={i} className="form-group-item">
                  <div className="form-group-header">
                    <span className="group-number">{i + 1}</span>
                    <button className="remove-item-btn" onClick={() => handleArrayRemove('awards', i)}>×</button>
                  </div>
                  <input {...commonInput} type="text" placeholder="Award Title"
                    value={award.title}
                    onChange={(e) => handleArrayFieldChange('awards', i, 'title', e.target.value)} />
                  <input {...commonInput} type="text" placeholder="Issuing Organization"
                    value={award.issuer}
                    onChange={(e) => handleArrayFieldChange('awards', i, 'issuer', e.target.value)} />
                  <input {...commonInput} type="text" placeholder="Date"
                    value={award.date}
                    onChange={(e) => handleArrayFieldChange('awards', i, 'date', e.target.value)} />
                  <textarea {...commonInput} placeholder="Description (optional)"
                    value={award.description}
                    onChange={(e) => handleArrayFieldChange('awards', i, 'description', e.target.value)} />
                </div>
              ))}
            </div>
          )}
        </AccordionSection>
          </div>
        );
      })}
    </div>
  );
};

export default ResumeForm;
